import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Star, Clock, ChevronDown } from 'lucide-react';
import { api } from '../../config/api';

const getDefaultHeroConfig = () => {
  const hour = new Date().getHours();
  if (hour >= 8 && hour < 12) return {
    headline: 'Start Your Morning Right',
    subtext: 'Fresh breakfast specials and juices brewed and grilled to perfection',
    ctaPrimary: { text: 'View Breakfast Menu', to: '/menu' },
    ctaSecondary: { text: 'Reserve Table', to: '/booking' },
    bg: 'https://images.unsplash.com/photo-1600271886742-f049cd451bba?w=1920&q=80'
  };
  if (hour >= 12 && hour < 16) return {
    headline: 'Sizzling Lunch Grills',
    subtext: 'Charcoal-grilled meat and fresh flavors — your midday escape awaits',
    ctaPrimary: { text: 'Explore Lunch Specials', to: '/menu' },
    ctaSecondary: { text: 'Reserve Table', to: '/booking' },
    bg: 'https://images.unsplash.com/photo-1544025162-d76694265947?w=1920&q=80'
  };
  if (hour >= 16 && hour < 22) return {
    headline: 'Juicy Grilled Dinner',
    subtext: 'Charcoal-grilled steak and chicken, cooked to perfection every evening',
    ctaPrimary: { text: 'Order Dinner', to: '/menu' },
    ctaSecondary: { text: 'Reserve Table', to: '/booking' },
    bg: 'https://images.unsplash.com/photo-1600891964092-4316c288032e?w=1920&q=80'
  };
  return {
    headline: 'Grilled to Perfection',
    subtext: 'From flame-grilled meat to fresh burgers — taste the flavor of Syra',
    ctaPrimary: { text: 'View Menu', to: '/menu' },
    ctaSecondary: { text: 'Make a Reservation', to: '/booking' },
    bg: 'https://images.unsplash.com/photo-1568901346375-23c9450c58cd?w=1920&q=80'
  };
};

const ytIdFromUrl = (url) => {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{6,})/);
  return m ? m[1] : null;
};
const isMp4 = (url) => /\.(mp4|webm|ogg|mov)(\?.*)?$/i.test(url || '');

const Hero = () => {
  const [settings, setSettings] = useState(null);
  const fallback = getDefaultHeroConfig();

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await api.get('/settings.php');
        if (res.data.success) setSettings(res.data.data);
      } catch (e) { /* use fallback */ }
    };
    fetchSettings();
  }, []);

  // Hero background: uploaded video > uploaded image > time-based default
  const video = settings?.hero_video || '';
  const poster = settings?.hero_poster || settings?.hero_image || '';
  const bg = settings?.hero_image || video || fallback.bg;
  const ytId = ytIdFromUrl(video);
  const mp4 = isMp4(video);

  return (
    <section className="hero-section text-white" style={{
      position: 'relative', overflow: 'hidden',
      backgroundColor: '#0d0d0d', minHeight: '100vh',
      display: 'flex', alignItems: 'center'
    }}>
      {/* Full-screen autoplay background video (blinq-style) */}
      <div style={{ position: 'absolute', inset: 0, zIndex: 0 }}>
        {video && mp4 ? (
          <video
            autoPlay muted loop playsInline preload="auto" poster={poster || bg}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          >
            <source src={video} type="video/mp4" />
          </video>
        ) : video && ytId ? (
          <iframe
            title="Hero background"
            src={`https://www.youtube.com/embed/${ytId}?autoplay=1&mute=1&loop=1&playlist=${ytId}&controls=0&modestbranding=1&showinfo=0&rel=0&playsinline=1&iv_load_policy=3&fs=0&disablekb=1`}
            allow="autoplay; encrypted-media; picture-in-picture"
            tabIndex={-1}
            style={{
              position: 'absolute', top: '50%', left: '50%',
              width: '100vw', height: '56.25vw', minHeight: '100vh', minWidth: '177.77vh',
              transform: 'translate(-50%, -50%)', border: 0, pointerEvents: 'none'
            }}
          />
        ) : (
          <div style={{ width: '100%', height: '100%', background: `url("${bg}") center/cover no-repeat` }} />
        )}
        {/* Dark overlay so text stays readable */}
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(rgba(26,26,26,0.5), rgba(26,26,26,0.7))' }} />
      </div>

      <div className="container text-center" style={{ position: 'relative', zIndex: 2 }}>
        {/* Rating Badge */}
        <div className="animate-fadeInUp" style={{ animationDelay: '0.2s', opacity: 0 }}>
          <div className="d-inline-flex align-items-center gap-2 px-4 py-2 rounded-pill mb-4"
            style={{ backgroundColor: 'rgba(212, 175, 55, 0.15)', border: '1px solid rgba(212, 175, 55, 0.3)' }}>
            {[...Array(5)].map((_, i) => <Star key={i} size={14} fill="#D4AF37" color="#D4AF37" />)}
            <span style={{ fontSize: '0.8rem', letterSpacing: '2px', fontWeight: 500, color: '#D4AF37' }}>
              RATED RESTAURANT
            </span>
          </div>
        </div>

        {/* Title */}
        <h1 className="animate-fadeInUp" style={{
          animationDelay: '0.4s', opacity: 0,
          fontFamily: 'Playfair Display, serif',
          fontSize: 'clamp(2.5rem, 6vw, 5rem)',
          fontWeight: 700, lineHeight: 1.1, marginBottom: '8px', color: '#ffffff'
        }}>Syra</h1>
        <h2 className="animate-fadeInUp" style={{
          animationDelay: '0.6s', opacity: 0,
          fontFamily: 'Playfair Display, serif',
          fontSize: 'clamp(1.5rem, 3vw, 2.5rem)',
          fontWeight: 400, color: '#D4AF37', letterSpacing: '4px', marginBottom: '16px'
        }}>Food Point & Grill</h2>

        {/* Time-aware headline */}
        <h3 className="animate-fadeInUp" style={{
          animationDelay: '0.7s', opacity: 0,
          fontFamily: 'Playfair Display, serif',
          fontSize: 'clamp(1.3rem, 2.5vw, 2rem)',
          fontWeight: 600, color: '#ffffff', marginBottom: '8px'
        }}>{fallback.headline}</h3>

        <p className="animate-fadeInUp" style={{
          animationDelay: '0.8s', opacity: 0,
          fontSize: '1.1rem', maxWidth: '600px', margin: '0 auto 24px',
          lineHeight: 1.8, fontWeight: 300, color: 'rgba(255,255,255,0.85)'
        }}>{fallback.subtext}</p>

        {/* Trust Strip */}
        <div className="animate-fadeInUp trust-strip" style={{ animationDelay: '0.9s', opacity: 0 }}>
          <span className="trust-item"><Star size={14} fill="#D4AF37" color="#D4AF37" /> 4.9</span>
          <span className="trust-item">420+ Reviews</span>
          <span className="trust-item">
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#27AE60', display: 'inline-block', animation: 'pulse-dot 2s infinite' }}></span>
            Open Now
          </span>
          <span className="trust-item">🚗 Delivery</span>
          <span className="trust-item">⏱ ~25 min</span>
        </div>

        {/* CTA Buttons */}
        <div className="animate-fadeInUp d-flex gap-4 justify-content-center flex-wrap mb-4"
          style={{ animationDelay: '1.1s', opacity: 0 }}>
          <Link to={fallback.ctaPrimary.to} className="btn btn-gold" style={{ padding: '16px 40px', fontSize: '0.9rem' }} data-track="menu_click">
            {fallback.ctaPrimary.text}
          </Link>
          <Link to={fallback.ctaSecondary.to} className="btn btn-outline-gold" style={{ padding: '16px 40px', fontSize: '0.9rem' }} data-track="reserve_click">
            {fallback.ctaSecondary.text}
          </Link>
        </div>

        {/* Open Hours */}
        <div className="animate-fadeInUp d-flex align-items-center justify-content-center gap-3"
          style={{ animationDelay: '1.3s', opacity: 0 }}>
          <Clock size={16} color="#D4AF37" />
          <span style={{ fontSize: '0.9rem', fontWeight: 500 }}>
            <span style={{ color: '#D4AF37', fontWeight: 600 }}>Open Today</span>
            <span style={{ opacity: 0.8, marginLeft: '8px' }}>10 AM – 10 PM</span>
          </span>
        </div>

        {/* Scroll Indicator */}
        <div className="mt-5 animate-fadeIn" style={{ animationDelay: '2s', opacity: 0 }}>
          <ChevronDown size={28} color="rgba(255,255,255,0.5)" style={{ animation: 'bounce 2s infinite' }} />
        </div>
      </div>
    </section>
  );
};

export const getHeroBackground = () => getDefaultHeroConfig().bg;
export default Hero;
